import React, { useState } from 'react';
import { Box, Flex, Text, VStack } from '@chakra-ui/react';
import * as Icons from 'react-icons/fa6';
import ImageComponent from './ImageComponent';
import DOMPurify from 'dompurify';

/**
 * Component to display content in various styles based on type.
 */
const ContentDisplayComponent = ({ type, items, selectedItem, onSelect, marginTop, marginBottom }) => {
  const [hoveredItem, setHoveredItem] = useState(null);
  const renderIcon = (iconName, variant) => {
    const IconComponent = Icons[iconName];
    if (!IconComponent) return null;

    return (
      <Box
        bg={`var(--${variant}-icon-bg-color, var(--icon-background-color, #E6FDFC))`}
        width={`var(--${variant}-icon-size, 40px)`}
        height={`var(--${variant}-icon-size, 40px)`}
        display="flex"
        justifyContent="center"
        alignItems="center"
        borderRadius={`var(--${variant}-icon-border-radius, 50%)`}
        overflow="hidden"
      >
        <IconComponent
          style={{
            width: `calc(var(--${variant}-icon-size, 40px) * 0.6)`,
            height: `calc(var(--${variant}-icon-size, 40px) * 0.6)`,
            color: `var(--${variant}-icon-color, var(--primary-color))`,
          }}
        />
      </Box>
    );
  };

  /**
   * Renders the content item (icon/image, title, and description) with ADA compliance.
  */
  const renderContent = (item, isCard) => {
    const isSelected = selectedItem === item.title?.text || item.description?.text  || '';
    const isHovered = hoveredItem === item.title?.text || item.description?.text  || '';

    return (
      <Flex
        key={item.title?.text || item.description?.text}
        width="100%"
        direction="row"
        align="flex-start"
        /*bg={
          isSelected
            ? 'var(--vibrant-purple, #F1E0FF)'
            : isHovered && isCard
              ? 'var(--vibrant-purple-15, #EFEFEF)'
              : 'transparent'
        }*/
        borderRadius={isCard ? 'md' : 'none'}
        boxShadow={isCard && !isHovered && !isSelected ? 'sm' : 'none'}
        p={isCard ? 4 : 0}
        cursor={isCard ? 'pointer' : 'default'}
        onMouseEnter={() => setHoveredItem(item.title?.text || item.description?.text  || '')}
        onMouseLeave={() => setHoveredItem(null)}
        onClick={
          isCard && onSelect ? () => onSelect(item.title?.text || item.description?.text  || '') : undefined
        }
        transition="background-color 0.2s ease"
        tabIndex={isCard ? 0 : -1}
        role={isCard ? 'option' : 'listitem'}
        aria-selected={isSelected}
        aria-label={item.title?.text  || item.description?.text  || 'Content Item'}
      >
        {/* Left Section (Icon or Image) */}
        <Box
          flex={isCard ? '20%' : '15%'}
          display="flex"
          justifyContent="center"
          alignItems="center"
          overflow="hidden"
          mt={item.image?.marginTop || 4}
          mb={item.image?.marginBottom || 0}
        >
          {item.image?.type === 'icon' ? (
            <span aria-hidden="true">
              {renderIcon(item.image.icon, item.image.variant)}
            </span>
          ) : (
            item.image?.url && (
             /* <img
                src={`${appConfig.ImageBlobBaseUrl}/${item.image.url}`}
                alt={item.image.alt || item.title?.text || item.description?.text}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: isCard ? 'cover' : 'contain',
                }}
              />*/
              <ImageComponent
                image={item.image}
                variant={'logo'}
                position={'center center'}
                marginTop={'0'}
                marginBottom={'0'}
              />
            )
          )}
        </Box>

        {/* Right Section (Content) */}
        <Box flex={isCard ? '80%' : '85%'} pl={4}>
          {/* Title */}
          {item.title?.text && (
            <Text
              fontFamily={`var(--${item.title.variant}-font-family, Sora)`}
              fontSize={`var(--${item.title.variant}-font-size, 16px)`}
              fontWeight={`var(--${item.title.variant}-font-weight, bold)`}
              color={`var(--${item.title.variant}-color, #203433)`}
              mb={item.title.marginBottom || 0}
              id={`title-${item.title.text}`}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.title.text) }}
            />
          )}

          {/* Description */}
          {item.description?.text && (
            <Text
              fontFamily={`var(--${item.description.variant}-font-family, Roboto)`}
              fontSize={`var(--${item.description.variant}-font-size, 14px)`}
              fontWeight={`var(--${item.description.variant}-font-weight, normal)`}
              color={`var(--${item.description.variant}-color, #001A1A)`}
              aria-describedby={`title-${item.description.text}`}
              mt={item.description.marginTop || '-0.25rem'}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.description.text) }}
            />
          )}
        </Box>
      </Flex>
    );
  };

  switch (type) {
    case 'content-list':
      return (
        <VStack align="stretch" spacing={4} mt={marginTop || 2} mb={marginBottom || 0}>
          {items.map((item) => renderContent(item, false))}
        </VStack>
      );

    case 'content-card':
      return (
        <VStack align="stretch" spacing={4} mt={marginTop || 2} mb={marginBottom || 0}>
          {items.map((item) => renderContent(item, true))}
        </VStack>
      );

    default:
      return (
        <Text color="red.500">
          Unknown content type: {type}. Please use 'content-list' or
          'content-card'.
        </Text>
      );
  }
};

export default ContentDisplayComponent;
